import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  MoreHorizontal,
  Search,
  CirclePlus,
  Trash2,
  TriangleAlert,
  LogOut,
  Pencil,
  BadgeCheck,
} from "lucide-react";
import pipaanlogo from "../../assets/pipaanlogo.png";
import userimg from "../../assets/user.png";
import closecircle from "../../assets/images/icons/close-circle.png";
import profilebanner from "../../assets/images/Profile-banner.png";
import { fetchOwnProfile } from "../../utils/auth";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const MEMBERS_PAGE_SIZE = 20;
const MAX_GROUP_NAME = 100;
const MAX_GROUP_INFO = 255;
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif"];
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const reportReasons = ["Spam", "Inappropriate Content", "Harassment", "Fake Group", "Other"];

const getToken = () => sessionStorage.getItem("accessToken") || localStorage.getItem("accessToken");

const formatCreatedOn = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

const displayName = (user) =>
  [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() || user?.username || "Member";

const Adminpanel = ({ matchedGroup, onGroupDelete, setShowDashboard, setActiveTab }) => {
  const [isEditingGroup, setIsEditingGroup] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [membersTotal, setMembersTotal] = useState(0);
  const [membersPage, setMembersPage] = useState(1);
  const [membersLoading, setMembersLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const searchTimeout = useRef(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [modalSearch, setModalSearch] = useState("");
  const [modalResults, setModalResults] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [editedGroup, setEditedGroup] = useState({
    group_name: "",
    description: "",
    group_image: "",
    group_cover: "",
  });
  const groupId = matchedGroup?.id;
  const membersRequestRef = useRef(0);

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    let cancelled = false;
    fetchOwnProfile(token)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && data?.result) setCurrentUser(data.result);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchGroupMembers = useCallback(
    async (searchValue = "", page = 1) => {
      if (!groupId) return;
      const requestId = ++membersRequestRef.current;
      setMembersLoading(true);
      try {
        const { data } = await axios.post(
          `${API_BASE_URL}/group_member/get-group_members`,
          {
            filters: {
              group_id: String(groupId),
              status: "accepted",
              is_deleted: false,
              ...(searchValue.trim() ? { search: searchValue.trim() } : {}),
            },
            page,
            limit: MEMBERS_PAGE_SIZE,
          },
          { headers: { token: getToken() } }
        );
        if (requestId !== membersRequestRef.current) return;
        const rows = data?.result?.group_members || [];
        setGroupMembers((prev) => (page === 1 ? rows : [...prev, ...rows.filter((r) => !prev.some((p) => p.id === r.id))]));
        setMembersTotal(Number(data?.result?.total) || 0);
        setMembersPage(page);
      } catch {
        if (requestId === membersRequestRef.current && page === 1) setGroupMembers([]);
      } finally {
        if (requestId === membersRequestRef.current) setMembersLoading(false);
      }
    },
    [groupId]
  );

  useEffect(() => {
    setMemberSearch("");
    setOpenDropdownId(null);
    fetchGroupMembers("", 1);
  }, [fetchGroupMembers]);

  useEffect(() => () => clearTimeout(searchTimeout.current), []);

  // Keyed on the editable fields, not the object, so live updates (e.g. unread counts) don't reset an open edit form
  const { group_name: groupName, description: groupDescription, group_image: groupImage, group_cover: groupCover } =
    matchedGroup || {};
  useEffect(() => {
    if (!groupId) return;
    setEditedGroup({
      group_name: groupName || "",
      description: groupDescription || "",
      group_image: groupImage || "",
      group_cover: groupCover || "",
    });
  }, [groupId, groupName, groupDescription, groupImage, groupCover]);

  useEffect(() => {
    if (!openDropdownId) return;
    const close = () => setOpenDropdownId(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [openDropdownId]);

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(""), 3000);
    return () => clearTimeout(timer);
  }, [notice]);

  if (!matchedGroup) {
    return <p className="text-gray-500 text-center py-10">This group is no longer available.</p>;
  }

  const entityLabel = matchedGroup.is_family_group ? "Family" : "Group";
  const isCurrentUserAdmin =
    matchedGroup.myRole === "admin" ||
    (currentUser?.id != null && Number(matchedGroup.created_by) === Number(currentUser.id));
  const hasMoreMembers = groupMembers.length < membersTotal;

  const backToChat = () => {
    setActiveTab?.("Groupchat");
    setShowDashboard?.(true);
  };

  const handleMemberSearch = (e) => {
    const value = e.target.value.slice(0, 100);
    setMemberSearch(value);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => fetchGroupMembers(value, 1), 400);
  };

  const searchFriends = async (value) => {
    if (!value.trim()) {
      setModalResults([]);
      return;
    }
    setModalLoading(true);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/user/get-all-users`,
        { filters: { isFriendsOnly: true, search: value.trim() }, page: 1, limit: 10 },
        { headers: { token: getToken() } }
      );
      setModalResults(data?.success ? data.result.users || [] : []);
    } catch {
      setModalResults([]);
    }
    setModalLoading(false);
  };

  const handleModalSearch = (e) => {
    const value = e.target.value.slice(0, 100);
    setModalSearch(value);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => searchFriends(value), 400);
  };

  const openAddMember = () => {
    setSelectedUserId(null);
    setShowModal(true);
    searchFriends(modalSearch);
  };

  const closeAddMember = () => {
    setShowModal(false);
    setModalSearch("");
    setModalResults([]);
    setSelectedUserId(null);
  };

  const handleAddMember = async () => {
    if (!selectedUserId || busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/group_member/add-group_member`,
        { group_id: String(groupId), user_id: String(selectedUserId), role: "member" },
        { headers: { token: getToken() } }
      );
      if (data?.success) {
        closeAddMember();
        setNotice("Invitation sent. They will appear here once they accept.");
      } else {
        alert("Failed to add member: " + (data?.message || "Unknown error"));
      }
    } catch (error) {
      alert(error.response?.data?.message || error.message);
    } finally {
      setBusy(false);
    }
  };

  const handleMakeAdmin = async (member) => {
    setOpenDropdownId(null);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/group_member/edit-group_member`,
        { group_member_id: member.id, user_id: member.user.id, role: "admin" },
        { headers: { token: getToken() } }
      );
      if (data?.success) {
        setNotice(`${displayName(member.user)} is now an admin.`);
        fetchGroupMembers(memberSearch, 1);
      } else {
        alert("Failed to make admin: " + (data?.message || "Unknown error"));
      }
    } catch (error) {
      alert(error.response?.data?.message || error.message);
    }
  };

  const removeMembership = (userId) =>
    axios.post(
      `${API_BASE_URL}/group_member/delete-group_member-by-userid`,
      { group_id: groupId, user_id: userId },
      { headers: { token: getToken() } }
    );

  const handleRemoveMember = async (member) => {
    setOpenDropdownId(null);
    if (!window.confirm(`Remove ${displayName(member.user)} from this ${entityLabel.toLowerCase()}?`)) return;
    try {
      const { data } = await removeMembership(member.user.id);
      if (data?.success) {
        setNotice("Member removed.");
        fetchGroupMembers(memberSearch, 1);
      } else {
        alert("Failed to remove member: " + (data?.message || "Unknown error"));
      }
    } catch (error) {
      alert(error.response?.data?.message || error.message);
    }
  };

  const handleLeaveGroup = async () => {
    setOpenDropdownId(null);
    if (!currentUser?.id) return;
    if (!window.confirm(`Are you sure you want to leave this ${entityLabel.toLowerCase()}?`)) return;
    try {
      const { data } = await removeMembership(currentUser.id);
      if (data?.success) {
        alert(`You have left the ${entityLabel.toLowerCase()}.`);
        onGroupDelete?.(groupId);
      } else {
        alert("Failed to leave: " + (data?.message || "Unknown error"));
      }
    } catch (error) {
      alert(error.response?.data?.message || error.message);
    }
  };

  const handleGroupUpdate = async () => {
    const name = editedGroup.group_name.trim();
    const description = (editedGroup.description || "").trim();
    if (!name) {
      alert(`${entityLabel} Name is required.`);
      return;
    }
    if (name.length > MAX_GROUP_NAME) {
      alert(`${entityLabel} Name can have at most ${MAX_GROUP_NAME} characters.`);
      return;
    }
    if (description.length > MAX_GROUP_INFO) {
      alert(`${entityLabel} Info can have at most ${MAX_GROUP_INFO} characters.`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/group/edit-group`,
        {
          group_id: groupId,
          group_name: name,
          description,
          group_image: editedGroup.group_image,
          group_cover: editedGroup.group_cover,
          is_deleted: false,
        },
        { headers: { token: getToken() } }
      );
      if (data?.success) {
        alert(`${entityLabel} updated successfully`);
        setIsEditingGroup(false);
        window.location.reload();
      } else {
        alert(`Failed to update ${entityLabel.toLowerCase()}: ` + data?.message);
      }
    } catch (error) {
      alert(error.response?.data?.message || `An error occurred while updating the ${entityLabel.toLowerCase()}.`);
    } finally {
      setBusy(false);
    }
  };

  const uploadImage = async (file) => {
    const { data } = await axios.post(
      `${API_BASE_URL}/aws/generate-presigned-url`,
      { files: [{ fileName: file.name, fileType: file.type }] },
      { headers: { token: getToken() } }
    );
    const target = data?.directory?.[0];
    if (!target?.presignedUrl || !target?.fileUrl) throw new Error("Upload could not be started.");
    await axios.put(target.presignedUrl, file, { headers: { "Content-Type": file.type } });
    return target.fileUrl;
  };

  const handleImageChange = (field) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!IMAGE_TYPES.includes(file.type)) {
      alert("Please choose a JPG, PNG or GIF image.");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      alert("Image must be 10 MB or smaller.");
      return;
    }
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setEditedGroup((prev) => ({ ...prev, [field]: url }));
    } catch (err) {
      alert(err.response?.data?.message || "Failed to upload the image.");
    }
    setUploading(false);
  };

  const handleDeleteGroup = async () => {
    setShowDeleteConfirm(false);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/group/edit-group`,
        { group_id: groupId, is_deleted: true },
        { headers: { token: getToken() } }
      );
      if (data?.success) {
        alert(`${entityLabel} deleted successfully!`);
        onGroupDelete?.(groupId);
      } else {
        alert(`Failed to delete ${entityLabel.toLowerCase()}: ` + (data?.message || "Unknown error"));
      }
    } catch (error) {
      alert(error.response?.data?.message || error.message);
    }
  };

  const handleReportGroup = async () => {
    if (!reportReason || busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/reportgroup/add-reportgroup`,
        { group_id: String(groupId), description: reportReason },
        { headers: { token: getToken() } }
      );
      if (data?.success) {
        setShowReportModal(false);
        setReportReason("");
        setNotice(`${entityLabel} reported. Thank you for letting us know.`);
      } else {
        alert(`Failed to report ${entityLabel.toLowerCase()}: ` + (data?.message || "Unknown error"));
      }
    } catch (error) {
      alert(error.response?.data?.message || error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full">
      {/* Cover and title */}
      <div className="rounded-2xl overflow-hidden bg-white">
        <img
          src={matchedGroup.group_cover || profilebanner}
          alt={`${entityLabel} cover`}
          className="w-full h-[160px] sm:h-[200px] object-cover"
        />
      </div>
      <div className="flex flex-wrap items-end gap-3 px-3 -mt-12 sm:-mt-14">
        <img
          src={matchedGroup.group_image || userimg}
          alt={matchedGroup.group_name}
          className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl border-[3px] border-[#3ED3C6] bg-white object-cover shrink-0"
        />
        <div className="flex-1 min-w-0 pb-1">
          <h2 className="text-xl font-semibold text-[#000000] truncate">{matchedGroup.group_name}</h2>
          <p className="text-xs text-[#4B4B4B]">
            {Number(matchedGroup.acceptedMembersCount) || 0} Members
            {formatCreatedOn(matchedGroup.created_at) ? ` | Created on : ${formatCreatedOn(matchedGroup.created_at)}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2 pb-1">
          {isCurrentUserAdmin && (
            <button
              onClick={() => setIsEditingGroup(true)}
              className="flex items-center gap-1 text-xs font-semibold text-[#095228] border border-[#095228] bg-white px-3 py-1.5 rounded-full hover:bg-[#EBF8ED]"
            >
              <Pencil className="w-3.5 h-3.5" /> Edit
            </button>
          )}
          <button
            onClick={handleLeaveGroup}
            disabled={!currentUser}
            className="flex items-center gap-1 text-xs font-semibold text-white bg-[#095228] px-3 py-1.5 rounded-full hover:bg-[#0b6631] disabled:opacity-50"
          >
            <LogOut className="w-3.5 h-3.5" /> Exit {entityLabel}
          </button>
        </div>
      </div>

      {/* Info */}
      <div className="mx-3 mt-4 rounded-xl border border-[#9ED8E6] bg-gradient-to-r from-[#E6F7EC] to-[#DDF2F8] p-3">
        <h4 className="text-sm font-semibold text-[#000000] mb-1">{entityLabel} Info</h4>
        <p className="text-xs text-[#333333] whitespace-pre-wrap break-words">
          {matchedGroup.description || "No info added yet."}
        </p>
      </div>

      {/* Actions */}
      <div className="mx-3 mt-4 flex flex-wrap items-center gap-2">
        {isCurrentUserAdmin && (
          <div className="flex items-center bg-white border border-[#E0E0E0] rounded-full pl-3 pr-1 py-1">
            <Search className="w-4 h-4 text-gray-400 shrink-0" />
            <input
              type="text"
              value={modalSearch}
              onChange={(e) => setModalSearch(e.target.value.slice(0, 100))}
              onKeyDown={(e) => e.key === "Enter" && openAddMember()}
              placeholder="Find Friends"
              className="w-28 sm:w-32 ml-2 text-sm bg-transparent outline-none"
            />
            <button
              onClick={openAddMember}
              className="flex items-center gap-1 bg-[#D9D9D9] text-[#4B4B4B] text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-[#cfcfcf]"
            >
              <CirclePlus className="w-4 h-4" /> Add Member
            </button>
          </div>
        )}
        {isCurrentUserAdmin && (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="flex items-center gap-1.5 bg-[#FDF2F3] border border-[#BE1F2E] text-[#BE1F2E] text-xs font-semibold px-3 py-2 rounded-full hover:bg-[#F9E1E3]"
          >
            <Trash2 className="w-4 h-4" /> Delete
          </button>
        )}
        <button
          onClick={() => setShowReportModal(true)}
          className="flex items-center gap-1.5 bg-[#FDF2F3] border border-[#BE1F2E] text-[#BE1F2E] text-xs font-semibold px-3 py-2 rounded-full hover:bg-[#F9E1E3]"
        >
          <TriangleAlert className="w-4 h-4" /> Report
        </button>
      </div>

      {notice && <p className="mx-3 mt-3 text-xs text-[#095228] font-medium">{notice}</p>}

      {/* Members */}
      <p className="mx-3 mt-5 text-sm font-semibold text-[#095228]">
        {Number(matchedGroup.acceptedMembersCount) || 0} {entityLabel} Members | {Number(matchedGroup.adminCounts) || 0} Admins
      </p>
      <div className="mx-3 mt-3 relative max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder={`Find ${entityLabel} Members`}
          value={memberSearch}
          onChange={handleMemberSearch}
          className="w-full pl-9 pr-3 py-2.5 text-sm rounded-full bg-white border border-[#E0E0E0] outline-none focus:ring-2 focus:ring-green-400"
        />
      </div>

      <div className="mx-3 mt-3">
        {membersLoading && groupMembers.length === 0 && (
          <p className="text-sm text-gray-500 py-4">Loading members...</p>
        )}
        {!membersLoading && groupMembers.length === 0 && (
          <p className="text-sm text-gray-500 py-4">
            {memberSearch.trim() ? "No members match your search." : "No members yet."}
          </p>
        )}
        {groupMembers.map((member) => {
          const isSelf = member?.user?.id === currentUser?.id;
          const canManage = isCurrentUserAdmin && !isSelf;
          return (
            <div key={member.id} className="flex items-center gap-3 py-3 border-b border-[#E3E3E3]">
              <img
                src={member.user?.profile_picture_url || userimg}
                alt={displayName(member.user)}
                className="w-11 h-11 rounded-lg object-cover shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <p className="text-sm font-semibold text-[#000000] truncate">{displayName(member.user)}</p>
                  {member.role === "admin" && (
                    <span className="flex items-center gap-0.5 text-[10px] font-semibold text-[#39B54A] shrink-0">
                      <BadgeCheck className="w-3.5 h-3.5" /> Admin
                    </span>
                  )}
                </div>
                {member.user?.username && (
                  <p className="text-[11px] text-[#747474] truncate">@{member.user.username}</p>
                )}
                <p className="text-[11px] text-[#4B4B4B]">{Number(member.user?.followersCount) || 0} Followers</p>
              </div>
              {(canManage || isSelf) && (
                <div className="relative shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenDropdownId(openDropdownId === member.id ? null : member.id);
                    }}
                    className="flex items-center justify-center w-8 h-8 rounded-full border border-[#4B4B4B] text-[#4B4B4B] hover:bg-gray-100"
                    aria-label="Member options"
                  >
                    <MoreHorizontal size={16} />
                  </button>
                  {openDropdownId === member.id && (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      className="absolute right-0 top-9 w-44 bg-white border rounded-lg shadow-lg z-20 py-1 text-sm"
                    >
                      {canManage && member.role !== "admin" && (
                        <button
                          onClick={() => handleMakeAdmin(member)}
                          className="w-full text-left px-4 py-2 hover:bg-[#EBF8ED]"
                        >
                          Mark As {entityLabel} Admin
                        </button>
                      )}
                      {canManage && (
                        <button
                          onClick={() => handleRemoveMember(member)}
                          className="w-full text-left px-4 py-2 hover:bg-[#EBF8ED] text-[#BE1F2E]"
                        >
                          Remove
                        </button>
                      )}
                      {isSelf && (
                        <button onClick={handleLeaveGroup} className="w-full text-left px-4 py-2 hover:bg-[#EBF8ED]">
                          Leave {entityLabel}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {hasMoreMembers && (
          <button
            onClick={() => fetchGroupMembers(memberSearch, membersPage + 1)}
            disabled={membersLoading}
            className="w-full py-3 text-sm font-semibold text-[#095228] disabled:opacity-50"
          >
            {membersLoading ? "Loading..." : "Load more"}
          </button>
        )}
      </div>

      <div className="text-center py-3 lg:hidden">
        <button onClick={backToChat} className="text-sm font-semibold text-[#095228] underline">
          Back to {entityLabel} Chat
        </button>
      </div>

      {/* Add member */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] px-4" onClick={closeAddMember}>
          <div className="bg-white p-6 rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-4">Add Member</h2>
            <input
              autoFocus
              type="text"
              placeholder="Search your friends"
              className="w-full border border-gray-300 rounded-xl px-3 py-2 mb-4 outline-none focus:ring-2 focus:ring-green-400"
              value={modalSearch}
              onChange={handleModalSearch}
            />
            <div className="max-h-60 overflow-y-auto mb-4">
              {modalLoading ? (
                <p className="text-sm text-gray-500">Loading...</p>
              ) : !modalSearch.trim() ? (
                <p className="text-sm text-gray-500">Type a name to find a friend.</p>
              ) : modalResults.length === 0 ? (
                <p className="text-sm text-gray-500">No results found.</p>
              ) : (
                modalResults.map((user) => (
                  <button
                    type="button"
                    key={user.id}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg text-left hover:bg-gray-100 ${
                      selectedUserId === String(user.id) ? "bg-green-50 ring-2 ring-green-600" : ""
                    }`}
                    onClick={() => setSelectedUserId(String(user.id))}
                  >
                    <img
                      src={user.profile_picture_url || userimg}
                      alt={user.username}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div className="min-w-0">
                      <p className="font-medium truncate">{displayName(user)}</p>
                      <p className="text-sm text-gray-500 truncate">@{user.username}</p>
                    </div>
                  </button>
                ))
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={closeAddMember} className="px-4 py-2 bg-gray-200 text-black rounded-full text-sm">
                Cancel
              </button>
              <button
                onClick={handleAddMember}
                className="px-4 py-2 bg-[#095228] text-white rounded-full text-sm disabled:opacity-50"
                disabled={!selectedUserId || busy}
              >
                {busy ? "Adding..." : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] px-4">
          <div className="bg-white rounded-2xl p-8 shadow-lg max-w-sm w-full text-center">
            <h2 className="text-lg font-bold mb-4 text-[#BE1F2E]">Delete {entityLabel}</h2>
            <p className="mb-6 text-gray-700">
              Are you sure you want to delete this {entityLabel.toLowerCase()}? This action cannot be undone.
            </p>
            <div className="flex justify-center gap-4">
              <button className="bg-gray-200 text-black px-4 py-2 rounded-full" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </button>
              <button className="bg-[#BE1F2E] text-white px-4 py-2 rounded-full" onClick={handleDeleteGroup}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] px-4">
          <div className="bg-white rounded-2xl p-8 shadow-lg max-w-sm w-full text-center">
            <h2 className="text-lg font-bold mb-4 text-[#BE1F2E]">Report {entityLabel}</h2>
            <p className="mb-4 text-gray-700">Please select a reason for reporting this {entityLabel.toLowerCase()}:</p>
            <select
              className="w-full border border-gray-300 rounded-xl px-3 py-2 mb-4"
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
            >
              <option value="">Select reason</option>
              {reportReasons.map((reason) => (
                <option key={reason} value={reason}>
                  {reason}
                </option>
              ))}
            </select>
            <div className="flex justify-center gap-4">
              <button
                className="bg-gray-200 text-black px-4 py-2 rounded-full"
                onClick={() => {
                  setShowReportModal(false);
                  setReportReason("");
                }}
              >
                Cancel
              </button>
              <button
                className="bg-[#BE1F2E] text-white px-4 py-2 rounded-full disabled:opacity-50"
                disabled={!reportReason || busy}
                onClick={handleReportGroup}
              >
                Report
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit */}
      {isEditingGroup && (
        <div className="popup-share z-[99999] viewpost-popup fixed inset-0 flex items-center justify-center bg-[#000] bg-opacity-60 w-full backdrop-blur-md">
          <div className="border-popup bg-white rounded-lg relative max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between">
              <div className="logo-popup flex items-center justify-center space-x-2">
                <img src={pipaanlogo} alt="Nyawoo Logo" className="w-50" />
              </div>
              <button className="close-pop-x relative text-gray-500" onClick={() => setIsEditingGroup(false)}>
                <img src={closecircle} alt="Close" className="w-10 h-10" />
              </button>
            </div>
            <div className="max-w-3xl mx-auto p-4 crt-new-grp">
              <div className="space-y-4">
                <div className="text-center mb-5">
                  <h2 className="font-[600] text-[#000000] text-[30px]">Edit {entityLabel}</h2>
                </div>
                <div className="mb-4 text-left">
                  <label htmlFor="edit-group-image" className="block text-[#000000] font-medium mb-2">
                    {entityLabel} Image
                  </label>
                  <img
                    src={editedGroup.group_image || userimg}
                    alt={entityLabel}
                    className="w-32 h-32 object-cover rounded mb-2 border"
                  />
                  <input
                    id="edit-group-image"
                    type="file"
                    accept={IMAGE_TYPES.join(",")}
                    onChange={handleImageChange("group_image")}
                    className="block"
                    disabled={uploading}
                  />
                </div>
                <div className="mb-4 text-left">
                  <label htmlFor="edit-group-cover" className="block text-[#000000] font-medium mb-2">
                    {entityLabel} Cover Image
                  </label>
                  <img
                    src={editedGroup.group_cover || profilebanner}
                    alt={`${entityLabel} Cover`}
                    className="w-full h-32 object-cover rounded mb-2 border"
                  />
                  <input
                    id="edit-group-cover"
                    type="file"
                    accept={IMAGE_TYPES.join(",")}
                    onChange={handleImageChange("group_cover")}
                    className="block"
                    disabled={uploading}
                  />
                </div>
                <div className="mb-4 text-left">
                  <label htmlFor="group-name" className="block text-[#000000] font-medium mb-2">
                    {entityLabel} Name
                  </label>
                  <input
                    id="group-name"
                    type="text"
                    maxLength={MAX_GROUP_NAME}
                    value={editedGroup.group_name}
                    onChange={(e) => setEditedGroup({ ...editedGroup, group_name: e.target.value })}
                    className="w-full border border-[#095228] rounded-[12px] p-4 focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder={`Enter ${entityLabel} Name`}
                  />
                </div>
                <div className="mb-4 text-left">
                  <label htmlFor="group-description" className="block text-[#000000] font-medium mb-2">
                    {entityLabel} Info
                  </label>
                  <textarea
                    id="group-description"
                    maxLength={MAX_GROUP_INFO}
                    value={editedGroup.description}
                    onChange={(e) => setEditedGroup({ ...editedGroup, description: e.target.value })}
                    className="w-full border border-[#095228] rounded-[12px] p-4 focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder={`Enter ${entityLabel} Info`}
                  />
                  <p className="text-right text-[11px] text-gray-500">
                    {(editedGroup.description || "").length}/{MAX_GROUP_INFO}
                  </p>
                </div>
                <div className="flex gap-2 py-5">
                  <button
                    onClick={handleGroupUpdate}
                    disabled={uploading || busy}
                    className="bg-[#095228] text-white flex items-center px-4 py-2 rounded-full disabled:opacity-50"
                  >
                    {uploading ? "Uploading..." : "Update"}
                  </button>
                  <button
                    onClick={() => setIsEditingGroup(false)}
                    className="bg-gray-300 text-black flex items-center px-4 py-2 rounded-full"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Adminpanel;
